import { useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const StoryboardView = ({
  storyboards, selectedStoryboard, setSelectedStoryboard,
  generating, onAddScene,
}) => {
  const [showNewScene, setShowNewScene] = useState(false);
  const [newScene, setNewScene] = useState({
    scene_number: 1,
    prompt: "",
    duration: 4,
  });

  const handleAddScene = async (e) => {
    e.preventDefault();
    if (!selectedStoryboard || !newScene.prompt.trim()) return;
    await onAddScene(newScene);
    setNewScene({
      scene_number: (selectedStoryboard.scenes?.length || 0) + 2,
      prompt: "",
      duration: 4,
    });
    setShowNewScene(false);
  };

  return (
    <div className="space-y-4" data-testid="storyboards-list">
      {storyboards.length === 0 ? (
        <p className="text-center py-12 text-muted-foreground">
          No movies yet. Create your first storyboard to make a full movie!
        </p>
      ) : (
        storyboards.map((sb) => (
          <div
            key={sb.id}
            className="border border-border p-6 bg-surface"
            data-testid={`storyboard-${sb.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold">{sb.title}</h3>
                <p className="text-sm text-muted-foreground">{sb.description}</p>
                <p className="text-xs text-muted-foreground mt-2">{sb.scenes?.length || 0} scenes</p>
              </div>
              {selectedStoryboard?.id === sb.id && (
                <Button
                  onClick={() => setShowNewScene(!showNewScene)}
                  size="sm"
                  className="bg-white text-black hover:bg-gray-200"
                  data-testid="add-scene-button"
                >
                  <Plus size={16} className="mr-2" />
                  Add Scene
                </Button>
              )}
            </div>

            {selectedStoryboard?.id === sb.id && showNewScene && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mb-4 p-4 border border-border bg-black"
                data-testid="new-scene-form"
              >
                <form onSubmit={handleAddScene} className="space-y-3">
                  <Input
                    type="number"
                    value={newScene.scene_number}
                    onChange={(e) => setNewScene({ ...newScene, scene_number: parseInt(e.target.value) })}
                    placeholder="Scene number"
                    className="bg-transparent border-border"
                    data-testid="scene-number-input"
                  />
                  <Textarea
                    value={newScene.prompt}
                    onChange={(e) => setNewScene({ ...newScene, prompt: e.target.value })}
                    placeholder="Describe this scene..."
                    className="bg-transparent border-border min-h-[80px]"
                    data-testid="scene-prompt-input"
                  />
                  <select
                    value={newScene.duration}
                    onChange={(e) => setNewScene({ ...newScene, duration: parseInt(e.target.value) })}
                    className="w-full bg-transparent border border-border p-2 text-sm"
                  >
                    <option value={4} className="bg-black">4 seconds</option>
                    <option value={8} className="bg-black">8 seconds</option>
                    <option value={12} className="bg-black">12 seconds</option>
                  </select>
                  <Button
                    type="submit"
                    disabled={generating}
                    className="w-full bg-white text-black hover:bg-gray-200"
                    data-testid="generate-scene-button"
                  >
                    {generating ? "Generating Scene..." : "Generate Scene"}
                  </Button>
                </form>
              </motion.div>
            )}

            {sb.scenes && sb.scenes.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                {sb.scenes.map((scene) => (
                  <div
                    key={scene.id}
                    className="border border-border p-3 bg-black"
                    data-testid={`scene-${scene.id}`}
                  >
                    <div className="aspect-video bg-black/80 mb-2 flex items-center justify-center">
                      <span className="text-white/70 font-bold">{scene.scene_number}</span>
                    </div>
                    <p className="text-xs line-clamp-2 mb-1">{scene.prompt}</p>
                    <p className="text-xs text-muted-foreground">{scene.duration}s</p>
                  </div>
                ))}
              </div>
            )}

            <Button
              onClick={() => setSelectedStoryboard(selectedStoryboard?.id === sb.id ? null : sb)}
              variant="ghost"
              size="sm"
              className="mt-4"
              data-testid={`toggle-storyboard-${sb.id}`}
            >
              {selectedStoryboard?.id === sb.id ? "Collapse" : "Expand & Add Scenes"}
            </Button>
          </div>
        ))
      )}
    </div>
  );
};
